/**
 * Blossom client (BUD-01 auth, BUD-02 upload) — self-hostable, content-addressed
 * blob storage, replacing third-party anonymous file hosts.
 *
 * Why Blossom (review consensus):
 * - Content-addressed by SHA-256: the hash IS the integrity check and dedupes.
 * - Self-hostable next to the Phase-2 relay infra (same Tor hidden service).
 * - BUD-01 signed-event auth on upload/fetch — a server can gate blobs behind
 *   the same membership proof used for boards, instead of public-by-URL.
 * - No third-party ToS that prohibits adult content and nukes files on discovery.
 *
 * Auth events are signed with the caller's PER-BOARD pseudonymous key, so the
 * blob server never learns the member's identity pubkey either.
 *
 * Server config: one URL in localStorage ('relatos.blossom'). When unset, the
 * uploader falls back to the legacy third-party hosts (with their honest
 * warnings). Standing up the server itself is Phase-2 infra.
 */
import type { EventTemplate, NostrEvent } from 'nostr-tools';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

const LS_BLOSSOM = 'relatos.blossom';

export function getBlossomServer(): string | null {
  try {
    const u = (localStorage.getItem(LS_BLOSSOM) ?? '').trim().replace(/\/+$/, '');
    return u.startsWith('https://') || u.startsWith('http://') ? u : null;
  } catch { return null; }
}

export function setBlossomServer(url: string): void {
  const u = url.trim().replace(/\/+$/, '');
  if (!u) localStorage.removeItem(LS_BLOSSOM);
  else if (u.startsWith('https://') || u.startsWith('http://')) localStorage.setItem(LS_BLOSSOM, u);
  else throw new Error('Blossom server URL must start with https://');
}

/** BUD-01 authorization event template (kind 24242), expires in 5 minutes. */
export function buildUploadAuth(filename: string, sha256Hex: string): EventTemplate {
  return {
    kind: 24242,
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['t', 'upload'],
      ['x', sha256Hex],
      ['expiration', String(Math.floor(Date.now() / 1000) + 300)],
    ],
    content: `Upload ${filename}`,
  };
}

/**
 * BUD-02: PUT /upload with a base64'd BUD-01 auth event.
 * `authSign` signs the auth template — pass the per-board pseudo signer.
 * Returns the blob URL from the server descriptor.
 */
export async function blossomUpload(
  server: string,
  blob: Blob,
  filename: string,
  authSign: (tmpl: EventTemplate) => Promise<NostrEvent>,
): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const hash = bytesToHex(sha256(bytes));
  const auth = await authSign(buildUploadAuth(filename, hash));
  const res = await fetch(`${server}/upload`, {
    method: 'PUT',
    headers: {
      'Authorization': `Nostr ${btoa(JSON.stringify(auth))}`,
      'Content-Type': blob.type || 'application/octet-stream',
    },
    body: blob,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`Blossom upload failed: HTTP ${res.status}${msg ? ` — ${msg.slice(0, 120)}` : ''}`);
  }
  const desc = await res.json() as { url?: string; sha256?: string };
  if (!desc.url || !desc.url.startsWith('http')) throw new Error('Blossom server returned no URL');
  if (desc.sha256 && desc.sha256 !== hash) throw new Error('Blossom server hash mismatch');
  return desc.url;
}
